import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { generateUUID } from '@renderer/utils/idUtils'
import Preview from '@renderer/components/Preview'
import ComponentMapper from '@renderer/components/mapper/ComponentMapper'
import { useTranslation } from 'react-i18next'
import { Package, RecordingType, WelcomePageSettings } from '@renderer/types/configState'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { getNormalizedCaptureRatio as getSharedNormalizedCaptureRatio } from '@renderer/utils/captureRatioUtils'

// Import background image
import kotabaruCover from '/assets/images/kotabaru-welcome.png'

const WelcomePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { filters, config, getPageSettings } = useConfigStore()
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const { selectedCamera, selectedSecondaryCamera, cameraSettings } = useSettingsStore()
  const welcomeSettings = getPageSettings('welcome') as WelcomePageSettings | undefined
  const [isSessionStarting, setIsSessionStarting] = useState(false)
  const [animationTime, setAnimationTime] = useState(0)

  // Check if custom design should be used (from customTheme for newspaper themes)
  const useCustomDesign = config?.customTheme?.useCustomDesign ?? false
  const components = config?.pages?.welcome?.components
  const shouldUseComponentMapper = useCustomDesign && components && components.length > 0

  // Get first filter (monochrome)
  const firstFilter = filters[0]
  const filterAdjustments = firstFilter?.adjustments

  /** Must match the fixed preview wrapper (w-[930px] h-[430px]) for object-cover strip behavior. */
  const PREVIEW_BOX_W = 930
  const PREVIEW_BOX_H = 430

  const getNormalizedCaptureRatio = (): number | undefined =>
    getSharedNormalizedCaptureRatio(cameraSettings.captureRatio)

  // Animation for button (bounce effect - up and down)
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationTime((prev) => prev + 16)
    }, 16)
    return (): void => clearInterval(interval)
  }, [])

  const handleStart = async (): Promise<void> => {
    if (isSessionStarting) {
      return // Prevent multiple calls
    }
    setIsSessionStarting(true)

    try {
      console.log('[NEWSPAPER-KOTABARU WELCOME] Button clicked, starting session')
      if (!projectId) {
        console.error('[NEWSPAPER-KOTABARU WELCOME] No projectId found')
        setIsSessionStarting(false)
        return
      }

      const sessionId = generateUUID()
      const mode = 'capture' as const

      // Get recording package to determine type
      const recordingType = config?.pages?.checkout?.settings?.packages?.find(
        (pkg: Package) =>
          pkg.type === 'recording' ||
          pkg.type === 'strip_live' ||
          pkg.type === 'strip_story' ||
          pkg.type === 'session_recording'
      )?.type as RecordingType

      // Set session ID FIRST before any logs are created
      setSessionState({
        id: sessionId,
        projectId,
        mode,
        timeline: {
          startTime: new Date()
        },
        userPreferences: {
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled,
          recordingType,
          filterId: firstFilter?._id // Set first filter as default
        },
        gallery: {
          id: `${9999999999999 - Date.now()}-${sessionId}`
        }
      })

      // Now log activity with the session ID set
      logActivity(`Session started in ${mode} mode.`)

      // Navigation logic matches newspaper Welcome exactly
      const frameEnabled = config?.pages.frame.settings.enabled || false
      const filterSettings = getPageSettings('filter')
      const applyAfterCapture = filterSettings?.applyAfterCapture || false

      let nextPage: string

      if (welcomeSettings?.startWithPayment) {
        // If startWithPayment is enabled, always go to checkout first
        nextPage = 'checkout'
      } else {
        if (applyAfterCapture) {
          if (frameEnabled) {
            nextPage = 'frame'
          } else {
            nextPage = 'capture'
          }
        } else {
          nextPage = 'filter'
        }
      }

      // Check for secondary camera
      if (
        selectedSecondaryCamera &&
        nextPage === 'filter' &&
        (await unifiedCameraUtils.isDeviceResponding('secondary'))
      ) {
        nextPage = 'camera'
      }

      // Delay session upload to allow more logs to be collected
      setTimeout(() => {
        uploadSessionState()
      }, 2000)

      console.log('[NEWSPAPER-KOTABARU WELCOME] Navigating to:', `/${projectId}/${nextPage}`)
      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('[NEWSPAPER-KOTABARU WELCOME] Error starting session:', error)
    } finally {
      setIsSessionStarting(false)
    }
  }

  // If custom design is enabled and components exist, use ComponentMapper
  if (shouldUseComponentMapper) {
    return (
      <div className="relative w-full h-full">
        {components && <ComponentMapper components={components} />}
      </div>
    )
  }

  // Otherwise, use the default newspaper-kotabaru layout
  return (
    <div
      className="w-full h-screen relative overflow-hidden"
      style={{ width: '1080px', height: '1920px' }}
    >
      {/* Background Image - contain to span whole screen */}
      <div
        className="absolute inset-0 w-full h-full bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${kotabaruCover})`,
          backgroundSize: 'contain',
          backgroundPosition: 'top center'
        }}
      />

      {/* Preview Component - 1/4 height and full width */}
      <div className="absolute top-[410px] left-[90px] flex items-center justify-center z-10">
        <div className="w-[930px] h-[430px]">
          <Preview
            key={`preview-${selectedCamera || 'default'}`}
            adjustments={filterAdjustments}
            ratio={PREVIEW_BOX_W / PREVIEW_BOX_H}
            cameraRatio={getNormalizedCaptureRatio()}
            cameraKey="primary"
            filter={firstFilter?._id}
            fillContainer={true}
          />
        </div>
      </div>

      {/* Button - Bottom Center (matching newspaper Welcome) */}
      <div className="absolute bottom-28 left-1/2 z-20 flex justify-center w-full -translate-x-1/2">
        {((): React.ReactNode => {
          // Bounce animation - up and down movement
          const duration = 1000 // 1 second per cycle
          const progress = (animationTime % duration) / duration
          const bounceY = Math.sin(progress * Math.PI * 2) * 50 // 50px vertical movement
          const transformStyle = `translateY(${bounceY}px)`

          return (
            <button
              type="button"
              onClick={handleStart}
              disabled={isSessionStarting}
              style={{ transform: transformStyle }}
              className="hover:opacity-90 cursor-pointer font-serif font-bold transition-all duration-500 select-none active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-[#1a1a1a] text-[#fafaf8] border-2 border-double border-[#2a2a2a] text-7xl px-16 py-5 min-w-[320px] tracking-wider rounded-none shadow-none"
            >
              {isSessionStarting
                ? t('common.loading', 'Starting...')
                : t('newspaper.welcome.takePhoto', 'Take Photo')}
            </button>
          )
        })()}
      </div>
    </div>
  )
}

export default WelcomePage

